import { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Button, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  useAddMerchantMutation,
  useUpdateMerchantMutation,
  useGetMerchantCategoriesQuery,
  useAddMerchantCategoryMutation,
} from "../../../services/merchant";
import "./styles.scss";

interface MerchantFormValues { name: string; mobile: string; categoryId: string; }

interface AddMerchantModalProps {
  visible:    boolean;
  onCancel:   () => void;
  onSuccess?: (merchant: { _id?: string; name?: string; mobile?: string }) => void;
  // When set, the modal edits this merchant instead of creating a new one.
  // `category` here is the merchant's current category *name* — resolved to
  // its _id via categoryList below once categories have loaded.
  merchant?:  { _id: string; name?: string; mobile?: string; category?: string } | null;
}

const AddMerchantModal = ({ visible, onCancel, onSuccess, merchant }: AddMerchantModalProps) => {
  const [form] = Form.useForm<MerchantFormValues>();
  const isEdit = !!merchant?._id;
  const [addMerchant, { isLoading: adding }]     = useAddMerchantMutation();
  const [updateMerchant, { isLoading: updating }] = useUpdateMerchantMutation();
  const submitting = adding || updating;

  const { data: categoriesResp, isFetching: categoriesLoading } = useGetMerchantCategoriesQuery();
  const [addMerchantCategory, { isLoading: addingCategory }] = useAddMerchantCategoryMutation();
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  const categoryList = Array.isArray(categoriesResp)
    ? categoriesResp
    : (Array.isArray((categoriesResp as any)?.data) ? (categoriesResp as any).data : []);
  const categoryOptions = categoryList.map((c: any) => ({ value: c._id, label: c.name }));

  useEffect(() => {
    if (!visible) return;
    const matchedCategory = merchant?.category
      ? categoryList.find((c: any) => c.name?.toLowerCase() === merchant.category?.toLowerCase())
      : null;
    form.setFieldsValue({
      name:       merchant?.name || '',
      mobile:     merchant?.mobile || '',
      categoryId: matchedCategory?._id || '',
    });
  }, [visible, merchant, categoryList, form]);

  const handleClose = () => {
    form.resetFields();
    onCancel();
  };

  const handleSubmit = async (values: MerchantFormValues) => {
    try {
      if (isEdit) {
        await updateMerchant({ id: merchant!._id, ...values }).unwrap();
        // message.success('Merchant updated successfully');
        onSuccess?.({ _id: merchant!._id, ...values });
      } else {
        const res = await addMerchant(values).unwrap();
        // message.success('Merchant added successfully');
        onSuccess?.((res as any)?.data ?? values);
      }
      handleClose();
    } catch {
      /* error toast handled globally by badRequestHandler */
    }
  };

  const handleAddCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      const res = await addMerchantCategory({ name }).unwrap();
      const newId = (res as any)?.data?._id ?? (res as any)?._id;
      // message.success('Category added successfully');
      if (newId) form.setFieldsValue({ categoryId: newId });
      setNewCategoryName('');
      setAddCategoryOpen(false);
    } catch {
      /* error toast handled globally by badRequestHandler */
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      title={isEdit ? 'Edit Merchant' : 'Add Merchant'}
      okText={isEdit ? 'Save' : 'Add'}
      onOk={() => form.submit()}
      confirmLoading={submitting}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} className="add-merchant-form">
        <Form.Item name="name" label="Merchant Name" rules={[{ required: true, message: 'Please enter merchant name' }]}>
          <Input placeholder="Enter merchant name" />
        </Form.Item>

        <Form.Item
          name="mobile"
          label="Mobile Number"
          rules={[
            { required: true, message: 'Please enter mobile number' },
            { pattern: /^\d{10}$/, message: 'Enter a valid 10-digit mobile number' },
          ]}
        >
          <Input placeholder="Enter mobile number" maxLength={10} />
        </Form.Item>

        <Form.Item name="categoryId" label="Category" rules={[{ required: true, message: 'Please select a category' }]}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Select
              placeholder="Select category"
              loading={categoriesLoading}
              showSearch
              optionFilterProp="label"
              options={categoryOptions}
              style={{ flex: 1 }}
              value={Form.useWatch('categoryId', form)}
              onChange={(v) => form.setFieldsValue({ categoryId: v })}
            />
            <Button icon={<PlusOutlined />} onClick={() => setAddCategoryOpen(true)} />
          </div>
        </Form.Item>
      </Form>

      <Modal
        title="Add Category"
        open={addCategoryOpen}
        onCancel={() => { setAddCategoryOpen(false); setNewCategoryName(''); }}
        onOk={handleAddCategory}
        okText="Add"
        confirmLoading={addingCategory}
        destroyOnClose
      >
        <Input
          placeholder="e.g. Rent"
          value={newCategoryName}
          onChange={(e) => setNewCategoryName(e.target.value)}
          onPressEnter={handleAddCategory}
        />
      </Modal>
    </Modal>
  );
};

export default AddMerchantModal;
